import React, { useRef } from 'react';
import './SegmentedControl.css';

type SegmentedControlValue = string | number;
type SegmentedControlSize = 'sm' | 'md' | 'lg';

interface SegmentedControlOption<T extends SegmentedControlValue> {
    value: T;
    label: React.ReactNode;
    disabled?: boolean;
    ariaLabel?: string;
}

interface SegmentedControlProps<T extends SegmentedControlValue> {
    ariaLabel: string;
    value: T;
    options: Array<SegmentedControlOption<T>>;
    onChange: (value: T) => void;
    size?: SegmentedControlSize;
    fullWidth?: boolean;
    className?: string;
}

function SegmentedControl<T extends SegmentedControlValue>({
    ariaLabel,
    value,
    options,
    onChange,
    size = 'md',
    fullWidth = false,
    className,
}: SegmentedControlProps<T>) {
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const focusOption = (nextIndex: number, direction: 1 | -1) => {
        if (options.length === 0) {
            return;
        }

        for (let attempts = 0; attempts < options.length; attempts += 1) {
            const normalizedIndex = (nextIndex + attempts * direction + options.length) % options.length;
            const candidate = options[normalizedIndex];

            if (candidate?.disabled) {
                continue;
            }

            buttonRefs.current[normalizedIndex]?.focus();
            onChange(candidate.value);
            return;
        }
    };

    const classes = ['segmented-control', `segmented-control-size-${size}`, fullWidth ? 'segmented-control-full-width' : '', className ?? '']
        .filter(Boolean)
        .join(' ');

    return (
        <div className={classes} role="radiogroup" aria-label={ariaLabel}>
            {options.map((option, index) => {
                const isActive = option.value === value;

                return (
                    <button
                        key={option.value}
                        ref={(element) => {
                            buttonRefs.current[index] = element;
                        }}
                        type="button"
                        className={['segmented-control-option', isActive ? 'segmented-control-option-active' : ''].filter(Boolean).join(' ')}
                        role="radio"
                        aria-checked={isActive}
                        aria-label={option.ariaLabel}
                        tabIndex={isActive ? 0 : -1}
                        disabled={option.disabled}
                        onClick={() => onChange(option.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                                event.preventDefault();
                                focusOption(index + 1, 1);
                            }

                            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                                event.preventDefault();
                                focusOption(index - 1, -1);
                            }

                            if (event.key === 'Home') {
                                event.preventDefault();
                                focusOption(0, 1);
                            }

                            if (event.key === 'End') {
                                event.preventDefault();
                                focusOption(options.length - 1, -1);
                            }
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedControl;
